import React, { useEffect, useRef, useState } from 'react';
import { deleteConfirmCopy, jobDeleteCaution } from '../utils/adminJobCleanup';

const AdminJobsTable = ({ jobs, loading, error, deleting, onRefresh, onDeleteJobs }) => {
  const [selected, setSelected] = useState(() => new Set());
  const [confirming, setConfirming] = useState(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(jobs.map((job) => job.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [jobs]);

  useEffect(() => {
    setConfirming((current) => {
      if (!current) return current;
      const byId = new Map(jobs.map((job) => [job.id, job]));
      const next = current.map((job) => byId.get(job.id)).filter(Boolean);
      if (next.length === current.length && next.every((job, i) => job === current[i])) return current;
      return next.length ? next : null;
    });
  }, [jobs]);

  useEffect(() => {
    if (!confirming) return undefined;
    cancelRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape' && !deleting) setConfirming(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirming, deleting]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const askDelete = () => {
    const chosen = jobs.filter((job) => selected.has(job.id));
    if (chosen.length === 0) return;
    setConfirming(chosen);
  };

  const confirmDelete = async () => {
    if (!confirming || confirming.length === 0 || deleting) return;
    try {
      await onDeleteJobs(confirming.map((job) => job.id));
      setConfirming(null);
      setSelected(new Set());
    } catch {
      // The parent surfaces the error and refreshes the list.
    }
  };

  const copy = confirming ? deleteConfirmCopy(confirming.length) : null;
  const selectedCount = jobs.filter((job) => selected.has(job.id)).length;

  return (
    <div className="admin-jobs">
      <p className="admin-jobs-note">
        Tick the leftover jobs to remove, then confirm. Only the ticked jobs are deleted,
        together with the bids and other records that belong to them. Nothing here deletes every job.
      </p>
      <div className="admin-jobs-toolbar">
        <button type="button" className="btn btn-sm admin-refresh" onClick={onRefresh} disabled={loading || deleting}>
          ↻ Refresh jobs
        </button>
        <button
          type="button"
          className="btn btn-sm admin-btn-revoke"
          onClick={askDelete}
          disabled={selectedCount === 0 || deleting}
        >
          {deleting ? 'Deleting…' : `Delete selected (${selectedCount})`}
        </button>
      </div>

      {error && !confirming && (
        <div className="admin-fetch-error" role="alert">
          <strong>⚠️ Could not update jobs</strong>
          <code>{error}</code>
        </div>
      )}

      {loading && jobs.length === 0 && (
        <p className="text-muted" style={{ marginTop: '24px' }}>Loading jobs...</p>
      )}

      {!loading && !error && jobs.length === 0 && (
        <div className="card admin-empty-card">
          <p className="text-muted">No jobs.</p>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table admin-jobs-table">
            <thead>
              <tr>
                <th className="admin-jobs-select-col">Select</th>
                <th>Title</th>
                <th>Poster</th>
                <th>Created / job date</th>
                <th>Status</th>
                <th>Bids</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className={job.status === 'Pending' ? 'row-pending' : 'row-verified'}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${job.title}`}
                      checked={selected.has(job.id)}
                      disabled={deleting}
                      onChange={() => toggle(job.id)}
                    />
                  </td>
                  <td>
                    <div className="admin-carrier-name">{job.title}</div>
                  </td>
                  <td>
                    <div className="admin-jobs-poster">
                      <span className="admin-carrier-name">{job.posterName}</span>
                      <span className="admin-email">{job.posterEmail}</span>
                    </div>
                  </td>
                  <td>
                    <div>Created {job.createdLabel}</div>
                    <div className="admin-email">Job date {job.jobDateLabel}</div>
                  </td>
                  <td>
                    <span className={`badge ${job.status === 'Pending' ? 'badge-amber' : 'badge-teal'}`}>
                      {job.status}
                    </span>
                  </td>
                  <td>{job.bidCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming && copy && (
        <div
          className="admin-modal-backdrop"
          onClick={() => { if (!deleting) setConfirming(null); }}
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-job-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="admin-job-delete-title">{copy.title}</h2>
            <p>{copy.intro}</p>
            <p>{copy.leftover}</p>
            <p className="admin-email">{copy.untouched}</p>
            {error && <p className="admin-error" role="alert">{error}</p>}
            <ul className="admin-job-delete-list">
              {confirming.map((job) => {
                const caution = jobDeleteCaution(job);
                return (
                  <li key={job.id}>
                    <strong>{job.title}</strong>
                    <div className="admin-email">{job.posterName} · {job.posterEmail}</div>
                    <div className="admin-email">
                      Created {job.createdLabel} · Job date {job.jobDateLabel} · {job.status} · {job.bidCount} bid{job.bidCount === 1 ? '' : 's'}
                    </div>
                    {caution.length > 0 && (
                      <div className="admin-jobs-caution">{caution.join(' · ')}</div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="btn btn-sm admin-refresh"
                ref={cancelRef}
                onClick={() => setConfirming(null)}
                disabled={deleting}
              >
                {copy.cancel}
              </button>
              <button
                type="button"
                className="btn btn-sm admin-btn-revoke"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : copy.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobsTable;
